import { useContext } from 'react';
import { BaseControl } from '.';
import { DateRangeControlRenderer } from './DateRangeControl';
import { ReportPeriodContext } from '../context/ReportPeriodContext';

// AI-CONTEXT: See docs/features/livereport2-period-selector.md
// PeriodOverrideControl — a data widget's period setting, three states:
//   'inherit' — follow the report's overall period (default)
//   'custom'  — the widget's own start/end range
//   'all'     — explicitly all data, even when the report has a period
//
// Legacy values without a `mode` (pre-migration HouseVisits dateRange) derive
// it: dates present = custom, empty = inherit — the same mapping as
// resolveWidgetPeriod, so what the panel shows is what the data does.

/**
 * @typedef {object} PeriodOverrideValue
 * @property {'inherit'|'custom'|'all'} [mode]
 * @property {string|null} start - ISO date 'YYYY-MM-DD' or null
 * @property {string|null} end - ISO date 'YYYY-MM-DD' or null
 */

const MODES = [
    { key: 'inherit', label: 'Rapportperiode' },
    { key: 'all', label: 'Alles' },
    { key: 'custom', label: 'Aangepast' },
]

/** @param {string|null} iso @returns {string|null} 'YYYY-MM-DD' → 'DD-MM-YYYY' */
const isoToUI = (iso) => {
    if (!iso) return null
    const [y, m, d] = iso.split('-')
    return `${d}-${m}-${y}`
}

/** @param {{start: string|null, end: string|null}|null} period */
const formatInheritedPeriod = (period) => {
    const start = isoToUI(period?.start || null)
    const end = isoToUI(period?.end || null)
    if (start && end) return `${start} – ${end}`
    if (start) return `Vanaf ${start}`
    if (end) return `Tot ${end}`
    return null
}

/**
 * @extends {BaseControl<PeriodOverrideValue>}
 */
export class PeriodOverrideControl extends BaseControl {
    /** @type {BaseControl<PeriodOverrideValue>['render']} */
    render(value, onChange) {
        return (
            <PeriodOverrideControlRenderer
                value={value || { mode: 'inherit', start: null, end: null }}
                onChange={onChange}
                label={this.label}
                description={this.description}
            />
        )
    }
}

/**
 * @param {object} props
 * @param {PeriodOverrideValue} props.value - Current period setting
 * @param {(value: PeriodOverrideValue) => void} props.onChange - Fires on mode switch or date edit
 * @param {string} [props.label] - Optional label rendered above the buttons
 * @param {string} [props.description] - Optional description rendered below
 */
const PeriodOverrideControlRenderer = ({ value, onChange, label, description }) => {

    const reportPeriod = useContext(ReportPeriodContext)

    // Legacy values carry no mode — derive it the same way the data layer does.
    const mode = value.mode || ((value.start || value.end) ? 'custom' : 'inherit')

    const switchMode = (newMode) => {
        if (newMode === mode) return
        // Dates ride along on every switch so toggling modes is lossless —
        // resolveWidgetPeriod ignores start/end outside 'custom'. One caveat:
        // a value without a mode derives it from the dates, so writing the
        // mode explicitly here is what keeps non-custom states stable.
        onChange({ mode: newMode, start: value.start || null, end: value.end || null })
    }

    const inheritedLabel = formatInheritedPeriod(reportPeriod)

    return (
        <div className="period-override-control">
            {label && <span className="period-override-control-label">{label}</span>}
            <div className="period-override-control-modes">
                {MODES.map(m => (
                    <button
                        key={m.key}
                        type="button"
                        className={`period-override-control-mode${mode === m.key ? ' period-override-control-mode--active' : ''}`}
                        onClick={() => switchMode(m.key)}
                    >
                        {m.label}
                    </button>
                ))}
            </div>
            {mode === 'inherit' && (
                <p className="period-override-control-hint">
                    {inheritedLabel || 'Geen rapportperiode ingesteld — alle data wordt getoond.'}
                </p>
            )}
            {mode === 'custom' && (
                <DateRangeControlRenderer
                    value={{ start: value.start || null, end: value.end || null }}
                    onChange={(range) => onChange({ mode: 'custom', start: range.start, end: range.end })}
                />
            )}
            {description && <p className="period-override-control-description">{description}</p>}
        </div>
    )
}
